/**
 * What the browser player reports about the build it is actually running.
 *
 * This used to be a hardcoded `appVersion: "1.0.0"` on every heartbeat, which
 * is worse than silence. The server's client-release policy (api-server
 * lib/client-release.ts) treats an unparseable/absent version as unknown and
 * issues no verdict, but a literal reads as a real installed build: it can
 * clear a version floor the player never met, and the fleet page shows a
 * version nobody ever shipped. So this module reports only what it can prove,
 * and reports nothing otherwise.
 *
 * Field names match the flat heartbeat fields `parseClientReport()` reads
 * (api-server lib/device-telemetry-validation.ts). Unknown values are OMITTED
 * rather than sent as null, so the persisted `devices.client_info` row never
 * claims knowledge the player does not have.
 *
 * Two shells run this bundle: the plain browser player (surface "web") and the
 * Electron desktop shell, which loads the same player and exposes its real
 * binary version synchronously on `window.omnimediaDesktop` (surface
 * "desktop"). Neither has expo-updates, so there is no OTA identity to report:
 * a browser player picks up a new build on reload, which is why "web" reports
 * its updater as unsupported rather than pretending to have one.
 */

/** The two shells that run this bundle. Subset of the server's CLIENT_SURFACES. */
export type WebPlayerSurface = "web" | "desktop";

/** Heartbeat frame fields. Every optional key is omitted when unknown. */
export interface ClientReportFields {
  surface: WebPlayerSurface;
  appVersion?: string;
  updateStatus?: "unsupported";
}

export interface ClientBuildInputs {
  /** `window.omnimediaDesktop` is present (the Electron shell). */
  isDesktop: boolean;
  /** The Electron shell's real binary version, injected by its preload. */
  desktopAppVersion?: string | null;
  /** Version baked into the web bundle at build time, when the build sets one. */
  webBuildVersion?: string | null;
}

function clean(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Compose the report. Pure over its inputs so the honesty rules are testable:
 * no input is ever substituted with a literal, and an unknown value drops the
 * key entirely.
 */
export function buildClientReport(
  inputs: ClientBuildInputs,
): ClientReportFields {
  const webVersion = clean(inputs.webBuildVersion);
  if (inputs.isDesktop) {
    // The shell's binary version describes what the venue installed; the
    // bundle version is the fallback when an older preload does not expose it.
    const appVersion = clean(inputs.desktopAppVersion) ?? webVersion;
    return appVersion ? { surface: "desktop", appVersion } : { surface: "desktop" };
  }
  const report: ClientReportFields = { surface: "web", updateStatus: "unsupported" };
  if (webVersion) report.appVersion = webVersion;
  return report;
}

/** Build version compiled into the bundle, when the build defines one. */
function webBuildVersion(): string | null {
  // `import.meta.env` is a Vite construct; under the Node test runner it is
  // undefined, hence the guarded read (this module is imported by tests).
  const env = import.meta.env as Record<string, string | undefined> | undefined;
  return env?.["VITE_APP_VERSION"] ?? null;
}

/** Read the running shell's build identity. Safe to call outside a browser. */
export function readClientReport(): ClientReportFields {
  const desktop =
    typeof window === "undefined" ? undefined : window.omnimediaDesktop;
  return buildClientReport({
    isDesktop: desktop != null,
    desktopAppVersion: desktop?.appVersion ?? null,
    webBuildVersion: webBuildVersion(),
  });
}
